/**
 * Domain types — Smart Invoice Health (SIH)
 *
 * Authority: PRD-2026-001 v1.0, BR-1/BR-2/BR-3, ADR-000.
 *
 * LAYER LAW: this is the innermost layer. It imports nothing from the app.
 *
 * ADR-000 fitness F2 (NFR-M1 / QAS-5): sync-status derivation lives ONLY in the
 * API tier's BR-2 config module. The SPA renders statuses as delivered — these
 * types carry the derived status verbatim and contain ZERO derivation logic.
 * Any derivation attempt in the SPA is an architecture failure.
 */

/**
 * The BR-2 triad — exactly one of these. The BFF derives it (single config
 * module, NFR-M1); the SPA never computes or reclassifies it (BR-1).
 */
export type SyncStatus = 'SYNCED' | 'FAILED' | 'PENDING'

/**
 * One customer deployment (Smart Invoice stack), identified by its
 * `{TPIN}_{branch}` schema name (data-dictionary §1; api.yaml tinBranch
 * pattern `^[0-9]+_[0-9]+$`).
 */
export interface Deployment {
  /** Schema name in `{TPIN}_{branch}` form, e.g. "1000117278_002" (Mount Meru slice 1). */
  readonly dbName: string
  readonly unreachable: boolean
  /**
   * Named reason when unreachable (api.yaml: nullable string). Null/absent
   * when reachable. Rendered verbatim in the Unreachable state.
   */
  readonly unreachableReason: string | null
}

/**
 * FR-1 / SIH-2: per-deployment period summary.
 * Counts are claims (ux-principles §1): rendered verbatim from the BFF,
 * never estimated. Freshness is the source's last-data timestamp (FR-6,
 * ADR-000 F6) — formatted for display, never replaced by page-load time.
 */
export interface DeploymentSummary {
  readonly dbName: string
  readonly synced: number
  readonly failed: number
  readonly pending: number
  /** Source last-data timestamp (source format yyyyMMddHHmmss or ISO; rendered verbatim-ish via formatter). */
  readonly freshness: string | null
}

/**
 * FR-3 / SIH-3: one failed-invoice row. Columns are exactly the FR-3 contract
 * (design README S3 rule): identifier, customer/TPIN-branch, submission
 * timestamp, error code, error message. No invoice payload columns (QA-1
 * privacy note — `Json`/`responsedata` never render).
 */
export interface InvoiceRow {
  /** Per-branch document record number — the invoice identifier (data-dictionary §3). */
  readonly rectNo: number
  readonly tin: string
  readonly bhfId: string
  /** Invoice creation date (BR-3: owns the period), source format. */
  readonly createdDate: string
  /** Submission timestamp as delivered by the BFF (SIH-15 resolution carried server-side). Null where source holds none. */
  readonly submissionTimestamp: string | null
  /** Verbatim error code from source (e.g. "910"). Null where source holds none. */
  readonly resultCd: string | null
  /** Verbatim error message (lastError / responsedata-surfaced). Never paraphrased into fiscal advice (ux-principles §4). */
  readonly lastError: string | null
  /** Retry attempts recorded against the row (BR-2 derivation input, surfaced read-only). */
  readonly retryCount: number
  /** Derived status as delivered — the SPA renders it, never recomputes it (ADR-000 F2). */
  readonly derivedStatus: SyncStatus
}

/** One page of a cursor-paginated invoice list (api.yaml aggInvoices; R9). */
export interface InvoicePage {
  readonly tinBranch: string
  readonly invoices: readonly InvoiceRow[]
  /** Opaque cursor for the next page; null when exhausted. */
  readonly nextCursor: string | null
  readonly hasMore: boolean
}

/** Period selection (FR-5). Slice 1 mounts Today + presets + custom per design; BR-3: created date owns the period. */
export type PeriodPreset = 'TODAY' | 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'CUSTOM'

export interface PeriodSelection {
  readonly preset: PeriodPreset
  /** Inclusive range start, yyyy-MM-dd (custom preset only). */
  readonly from?: string
  /** Inclusive range end, yyyy-MM-dd (custom preset only). */
  readonly to?: string
}

/**
 * View-level real states (design README §6 real-state matrix). Every state is
 * first-class and designed — never a blank region or fake success
 * (ux-principles §3).
 */
export type ViewState<TData> =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'refreshing'; readonly previous: TData }
  | { readonly kind: 'ready'; readonly data: TData }
  | { readonly kind: 'error'; readonly message: string }

/**
 * A named API failure. The BFF returns a named error envelope
 * (api.yaml 503: "named error envelope"); the SPA surfaces it verbatim —
 * never swallow, never fake success (global hard rules).
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

/**
 * Invalid custom range (FR-5 fit criterion: custom range enforces from ≤ to).
 * Inline rejection state — never accepted silently.
 */
export class InvalidRangeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidRangeError'
  }
}
